import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, Popover } from 'antd';
import { Bell } from 'lucide-react';
import { toast } from 'sonner';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { NotificationPanel } from './NotificationPanel';
import { notificationRoute } from '../notificationRoute';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationFeed,
} from '../useNotifications';
import type { AppNotification } from '../types';

const BADGE_OVERFLOW = 99;

/** Topbar bildirim zili: polling'li rozet + tıklayınca dropdown panel + derin-link. */
export function NotificationBell() {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useNotificationFeed();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const unreadCount = data?.unreadCount ?? 0;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    // Açılışta taze liste (polling aralığını beklemeden).
    if (next) void refetch();
  };

  const handleSelect = (item: AppNotification) => {
    if (!item.isRead) markRead.mutate(item.id);
    const route = notificationRoute(item);
    setOpen(false);
    if (route) void navigate(route);
  };

  const handleMarkAll = () => {
    markAll.mutate(undefined, {
      onSuccess: (count) => toast.success(t('toast.markedAll', { count })),
      onError: (err) => toast.error(resolveApiErrorMessage(err)),
    });
  };

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      trigger="click"
      placement="bottomRight"
      arrow={false}
      styles={{ body: { padding: 0 } }}
      content={
        <NotificationPanel
          feed={data}
          loading={isLoading}
          error={isError}
          markingAll={markAll.isPending}
          onSelect={handleSelect}
          onMarkAll={handleMarkAll}
        />
      }
    >
      <Badge count={unreadCount} size="small" overflowCount={BADGE_OVERFLOW} offset={[-4, 4]}>
        <button
          type="button"
          className="ds-icon-btn"
          aria-label={t('bell.aria', { count: unreadCount })}
          data-testid="notification-bell"
        >
          <Bell size={16} strokeWidth={1.8} />
        </button>
      </Badge>
    </Popover>
  );
}
